import React, { useMemo, useState, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Paper,
  colors,
  Stack,
  InputLabel,
  Select,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";
import "./ContactUs.css";
import { useSelector } from "react-redux";
import { t } from "../../utils/i18n";
import ButtonSys from "../../common/ButtonSys";
import CheckIcon from "@mui/icons-material/Check";
import { ThemeContext } from "../../ThemeContext";

const Section = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundSize: "32px 32px",
  padding: "40px 0",
}));

const FormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginTop: "20px",
  maxWidth: "90%",
  mx: "auto",
}));

// Custom Styled TextField for exact design
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    // backgroundColor: "#f1ebfc",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 0, 0, 0.1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#000",
    },
    transition: "all 0.3s ease",
  },
  "& .MuiInputLabel-root": {
    color: "#000",
    fontSize: "14px",
    fontWeight: "500",
    position: "absolute",
    top: "12px",
    right: "12px",
    transform: "translate(0, -10px)",
    pointerEvents: "none",
    zIndex: 1,
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    // padding: "12px 16px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.1)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.1)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#000",
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: "#000",
  },
  "& .MuiFormLabel-root.Mui-error": {
    color: "#d32f2f",
  },
  "& .MuiFormHelperText-root": {
    marginLeft: "0",
    marginRight: "0",
    marginTop: "4px",
    fontSize: "12px",
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#000",
  color: "#fff",
  borderRadius: "20px",
  px: "24px",
  py: "12px",
  fontSize: "16px",
  fontWeight: "600",
  mt: "20px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#333",
  },
  "&:disabled": {
    opacity: 0.6,
  },
  minWidth: "100px",
  height: "40px",
}));

const currencies = [
  {
    value: "TC",
    label: "TC",
  },
  {
    value: "PM",
    label: "PM",
  },
  {
    value: "ART",
    label: "ART",
  },
];

const ContactUs = () => {
  const lang = useSelector((state) => state.i18n.lang);
  const [value, setValue] = useState(t(lang, "contact.request_offer"));
  const isAr = lang === "ar";
  const plansState = useSelector((state) => state.productData.plans);
  const { mode } = useContext(ThemeContext); 
  const [ticketInfo, setTicketInfo] = useState(null);
  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    plan: "",
    connectType: "",
    subject: "",
    message: "",
  });

  const plans = useMemo(() => {
  const raw = Array.isArray(plansState.data) ? plansState.data : [];
  const mapped = raw.map((p) => ({
    title: (lang === "ar" ? p?.name_ar : p?.name) || "",
  }));

  if (mapped.length === 0) {
    return [
      { title: "Basic" },
      { title: "Standard" },
      { title: "Premium" },
    ];
  }

  return mapped;
}, [plansState, lang]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitForm = async () => {

    if (
    !form.name ||
    !form.email ||
    !form.phone ||
    !form.plan ||
    form.plan === t(lang, "contact.request_offer") ||
    !form.connectType ||
    !form.subject ||
    !form.message
  ) {
    Swal.fire({
      icon: "warning",
      title: t(lang, "contact.validation_error", "Please fill all the details"),
      confirmButtonColor: "#d33",
    });
    return; 
  }
  try {
    Swal.fire({
      title: t(lang, "contact.sending_text"),
      text: t(lang, "contact.sending_message"),
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    const body = {
        helpdesk_ticket: {
          issue: {
            project_id: "crm",
            subject: form.subject || "Meeting Request",
            description: form.message || "ticket description",
            tracker_id: 31, 
            custom_field_values: {
              "1": "Any", 
              "2": window.location.origin,
              "42": form.subject || "Subject",
              "83": form.message || "Desc",
              "287": window.location.origin || "https://cyber.naahel.com", // Domain
              "291": form.message || "Event",
              "290": form.connectType, 
            },
          },
          contact: {
            email: form.email,
            phone: form.phone,
            first_name: form.name,
            middle_name: "",
            last_name: "", 
            company: form.organization,
            website: null,
            job_title: null,
            background: null,
            custom_field_values: {
              "18": "", 
              "59": "Lead", 
              "133": "",
              "288": "", 
            },
          },
          send_as: "auto_answer",
        },
      };

    const res = await fetch("https://track.dolfprojects.com/customapis/helpdesk_tickets.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Redmine-API-Key": process.env.REACT_APP_REDMINE_API_KEY,
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error("Failed to send");

    Swal.fire({
      icon: "success",
      title:t(lang, "contact.message_sent"),
      text: t(lang, "contact.messsage_text"),
      confirmButtonColor: "#3085d6",
    });

    const data = await res.json();

    setTicketInfo({
      id: data.helpdesk_ticket.id,
      content: data.helpdesk_ticket.content,
      contactName: data.helpdesk_ticket.contact?.name,
    });

    setForm({
      name: "",
      organization: "",
      email: "",
      phone: "",
      plan: "",
      connectType: "",
      subject: "",
      message: "",
    });

  } catch (err) {
    console.error(err);
    Swal.fire({
      icon: "error",
      title: t(lang, "contact.failed_message"),
      text: t(lang, "contact.failed_text"),
      confirmButtonColor: "#d33",
    });
  }
};



  return (
    <Section id="contact-us" mb={5}>
      {ticketInfo ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: { xs: 3, sm: 6 },
            backgroundColor: "#f9fafb",
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2.5,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#4caf50",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <CheckIcon sx={{ color: "#fff", fontSize: 40 }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: "#333", mb: 1 }}>
            {t(lang, "contact.ticket_submitted", "Your Ticket is Submitted!")}
          </Typography>

          <Typography variant="body2" sx={{ color: "#555", mb: 3 }}>
            {t(
              lang,
              "contact.ticket_confirmation_text",
              "Thank you! Your ticket has been successfully created. Here are the details:"
            )}
          </Typography>

          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              bgcolor: "#fff",
              borderRadius: "12px",
              p: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              textAlign: "left",
            }}
          >
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t(lang, "contact.ticket_id", "Ticket ID")}:</strong>{" "}
              {ticketInfo.id}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t(lang, "contact.content", "Content")}:</strong>{" "}
              {ticketInfo.content}
            </Typography>
            <Typography variant="body1">
              <strong>{t(lang, "contact.contact", "Contact")}:</strong>{" "}
              {ticketInfo.contactName}
            </Typography>
          </Box>
        </Box>
      ) : (
      <Box
        sx={{
          width: "90%",
          mx: "auto",
          background: mode === "light"
          ? `linear-gradient(
              135deg,
              rgba(251, 199, 184, 0.8) 0%,
              rgba(245, 218, 210, 0.6) 40%,
              rgba(219, 191, 186, 0.4) 70%,
              rgba(209, 96, 96, 0.2) 100%
            )`
          : `linear-gradient(
            135deg,
            rgba(30, 30, 30, 0.9) 0%,
            rgba(50, 50, 50, 0.7) 40%,
            rgba(70, 70, 70, 0.5) 70%,
            rgba(90, 90, 90, 0.3) 100%
          )`,
          borderRadius: 4, 
          p: 4,
        }}
      >
        {/* Header Text */}
        <Typography
          variant="h6"
          sx={{
            opacity: 0.8,
            fontWeight: 500,
            fontSize: "1.2rem",
          }}
        >
          {t(lang, "contact.title")}
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            mb: "0px !important",
            fontSize: "2.5rem",
          }}
        >
          {t(lang, "contact.sub-title", "Contact Us")}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            opacity: 0.8,

            fontSize: "1.2rem",
          }}
        >
          {t(lang, "contact.sub-enp-title")}
        </Typography>

        {/* Form Container */}
        <FormContainer maxWidth={"100% !important"}>
          <Box>
            {/* First Row: Institution Name & Name */}
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={3}
            >
              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "49.5%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.name")}
                id="name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
              />

              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "49.5%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.organization")}
                id="organization"
                value={form.organization}
                onChange={(e) => updateField("organization", e.target.value)}
              />
            </Stack>

            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={3}
            >
              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "49.5%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.email")}
                id="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
              />
              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "49.5%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                }}
                type="number"
                placeholder={t(lang, "contact.phone")}
                id="phone"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
              />
            </Stack>

            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={3}
            >
              <FormControl sx={{ width: "49.5%" }}>
                <Select
                    value={form.plan}
                    onChange={(e) => updateField("plan", e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#999", opacity: 0.7 }}>
                            {t(lang, "contact.request_offer")}
                          </span>
                        );
                      }
                      return selected;
                    }}
                    sx={{
                      width: "100%",
                      backgroundColor: (theme) => theme.palette.background.contactField,
                      borderRadius: "12px",
                      minHeight: "45px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#000",
                      },
                      "& .MuiSelect-select": {
                        color: (theme) => theme.palette.background.globalButton,
                      },
                    }}
                  >
                    {plans.map((plan, index) => (
                      <MenuItem key={index} value={plan.title}>
                        {plan.title}
                      </MenuItem>
                    ))}
                    <MenuItem value={t(lang, "contact.request_offer")}>
                      {t(lang, "contact.request_offer")}
                    </MenuItem>
                  </Select>
              </FormControl>
              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "49.5%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.connectType")}
                id="connectType"
                value={form.connectType}
                onChange={(e) => updateField("connectType", e.target.value)}
              />
            </Stack>

            {/* Fourth Row: Subject */}
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={3}
            >
              <TextField
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "100%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& input::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
                placeholder={t(lang, "contact.subject")}
                id="subject"
                value={form.subject}
                onChange={(e) => updateField("subject", e.target.value)}
              />
            </Stack>

            {/* Fifth Row: Message */}
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              mb={0}
            >
              <StyledTextField
                fullWidth
                placeholder={t(lang, "contact.message")}
                multiline
                value={form.message}
                onChange={(e) => updateField("message", e.target.value)}
                minRows={6}
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.background.contactField,
                  color: "#000",
                  width: "100%",
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                  "& textarea::placeholder": {
                    color: (theme) => theme.palette.background.globalButton,
                    opacity: 1,
                  },
                }}
              />
            </Stack>
          </Box>

          {/* Submit Button */}
          <ButtonSys
            onClick={submitForm}
            text={t(lang, "contact.send", "Send")}
          />
        </FormContainer>
      </Box>
      )}
    </Section>
  );
};

export default ContactUs;
